import React from 'react';
import { motion } from 'framer-motion';

const SecurityPage = () => {
  return (
    <div className="bg-zinc-950 grid-background min-h-screen text-zinc-100 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="prose prose-invert prose-zinc max-w-none"
        >
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Security and trust for enterprise automation</h1>
          <p className="text-zinc-400 mb-12">Doora is built for organizations that automate critical business processes and need strong, verifiable controls.</p>

          <p>This page gives security leadership and platform operations teams a practical view of our governance model, cloud architecture, and technical safeguards.</p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">Built for security leadership due diligence and platform operations implementation</h2>
          <p>Doora follows a defense-in-depth approach spanning policy, architecture, and operations. Our security program addresses governance expectations for compliance leaders and implementation requirements for engineering teams that run production workloads.</p>

          <h3 className="text-xl font-semibold mt-8 mb-4">For security leadership teams</h3>
          <ul className="list-disc pl-6 space-y-2 mb-4">
            <li>Security governance with clear ownership across CTO, Staff Engineering, and Product leadership.</li>
            <li>Policy lifecycle with formal review, approval, versioning, and communication for major updates.</li>
            <li>Control framework covering access, network, data, application, configuration, and incident response domains.</li>
            <li>Certification roadmap in progress for ISO 27001:2022, ISO 42001, and TISAX.</li>
          </ul>

          <h3 className="text-xl font-semibold mt-8 mb-4">For platform operations teams</h3>
          <ul className="list-disc pl-6 space-y-2 mb-4">
            <li>Cloud-native architecture on Azure AKS with modular services and secure traffic management.</li>
            <li>Network segmentation and customer-level isolation with dedicated VNets.</li>
            <li>Encryption and secret management integrated across storage, databases, and key infrastructure.</li>
            <li>Resilience strategy with backups, availability zones, and failover-ready infrastructure patterns.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-10 mb-4">Certification and assurance posture</h2>
          <p>Doora is actively pursuing major security and AI governance certifications, with targeted completion in the next 6-9 months. The certification program reflects our commitment to transparent, auditable, and repeatable security practices.</p>
          <ul className="list-disc pl-6 space-y-2 mb-4">
            <li>ISO 27001:2022 for a structured Information Security Management System (ISMS).</li>
            <li>ISO 42001 for trustworthy AI lifecycle governance, risk management, and impact assessment.</li>
            <li>TISAX for secure information handling, prototype protection, and GDPR-aligned controls.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-10 mb-4">Cloud assurance baseline</h2>
          <p>Doora runs on Microsoft Azure, which provides a broad compliance foundation including certifications and independent assurance frameworks such as ISO 27001, HITRUST, FedRAMP, and SOC reporting.</p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">Security governance and accountability</h2>
          <p>Information Security Policies and Guidelines are developed and maintained by Doora Staff Engineering in collaboration with Product and the CTO. Significant updates require CTO review and approval, followed by written internal communication.</p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">Identity and access management</h2>
          <p>Doora integrates dedicated instances per customer for identity and authentication, enabling strict tenant isolation and enterprise SSO compatibility.</p>
          <ul className="list-disc pl-6 space-y-2 mb-4">
            <li>Supports integration with existing SAML and OIDC identity providers.</li>
            <li>Uses role-based access principles and least privilege access patterns.</li>
            <li>Applies multi-factor authentication for management interfaces and privileged access paths.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-10 mb-4">Disaster recovery and operational resilience</h2>
          <p>Doora uses native resilience patterns to minimize downtime and support business continuity requirements across critical operations.</p>
          <ul className="list-disc pl-6 space-y-2 mb-4">
            <li>Zone-redundant storage: multiple copies across physical locations within a region.</li>
            <li>Automated backups: scheduled backup workflows for databases and critical application states.</li>
            <li>High availability: availability zones and region failover design patterns.</li>
          </ul>
        </motion.div>
      </div>
    </div>
  );
};

export default SecurityPage;
